import React, { FC, useMemo } from 'react'
import { Button } from '@/atoms/Button'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { Image } from '@/atoms/Image'
import { CaptionSM } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext'
import useCopyCode from '../../hooks/useCopyCode'
import DavidStyledText from '../DavidStyledText'
import { PaintBrushStroke } from '../PaintBrushStroke'
import { SpecificTimesToolTip } from '../ToolTips/SpecificTimesToolTip'

interface Props {
  open: boolean
  onClose: () => void
}

const code = 'KIDSGOFREE'
const logo = { url: '/v1761067809/theatrical/david/david-logo-color', width: 4000, height: 1001 }
const backgroundImage = { url: '/v1761058175/david/kids-go-free-modal-bg', width: 1520, height: 1864 }
const foregroundImage1 = {
  url: '/v1761058173/david/david-film_Zeruiah_9020s0302-char-v02_copy_1',
  width: 364,
  height: 640,
}
const foregroundImage2 = {
  url: '/v1761058173/david/david-film_Young_David_9020s0101-char-v01_copy_3',
  width: 464,
  height: 704,
}
const accentColor = '#EA6F13'
const accentColor2 = '#5C428F'

const DavidKidsGoFreeModal: FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslate('tickets')
  const { copiedCode, handleCopyCode } = useCopyCode(code)
  const { experiments } = useTheatricalPromotionContext()
  const useAltCopy = experiments?.['david-kidsgofree-promo'] ?? false

  const messageContent = useMemo(() => {
    if (useAltCopy) {
      return (
        <DavidStyledText className="whitespace-pre-line text-center text-[64px]" face="royal-loudes-rough">
          {t('freeTicketsForKids', 'free tickets\nfor kids')}
        </DavidStyledText>
      )
    }

    return (
      <>
        <DavidStyledText className="whitespace-nowrap text-center text-[28px]" face="royal-loudes-rough">
          {t('joinTheMovementToHelp', 'join the movement to help')}
        </DavidStyledText>
        <DavidStyledText className="whitespace-nowrap text-center text-[90px]" face="royal-loudes-rough">
          {t('oneMillion', '1 million')}
        </DavidStyledText>
        <DavidStyledText className="whitespace-nowrap text-center text-[28px]">
          {t('kidsGoFreeOnDec19', 'kids go free on dec 19')}
        </DavidStyledText>
      </>
    )
  }, [useAltCopy, t])

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={onClose}
      panelClasses="bg-white shadow-dark-4 px-6 py-8 rounded-3xl !max-w-[380px] relative text-center overflow-hidden"
      transition="appear"
      className="z-[99999]"
    >
      <Image
        className="absolute inset-0"
        alt="background image"
        src={backgroundImage.url}
        fill
        quality={50}
        sizes="100vw"
      />
      <Image
        className="absolute bottom-0 left-0"
        alt="child image"
        src={foregroundImage1.url}
        width={91}
        height={(foregroundImage1.height / foregroundImage1.width) * 91}
      />
      <Image
        className="absolute bottom-0 right-0"
        alt="child 2 image"
        src={foregroundImage2.url}
        width={116}
        height={(foregroundImage2.height / foregroundImage2.width) * 116}
      />
      <div className="absolute right-4 top-4 z-30 cursor-pointer rounded-full bg-black/10 p-2" onClick={onClose}>
        <CloseIcon color="black" size={16} />
      </div>
      <div className="relative z-10 flex flex-col items-center">
        <Image src={logo.url} width={184} height={(logo.height / logo.width) * 184} alt="david logo" />

        <div className="mt-6 flex flex-col flex-nowrap justify-center">{messageContent}</div>

        <div className="mt-6 flex flex-col items-center text-center">
          <DavidStyledText className="text-[28px]">{t('useCodeLowerCase', 'use code')}</DavidStyledText>
          <PaintBrushStroke color={accentColor}>
            <DavidStyledText color="white" className="block px-6 py-3 text-[40px] lowercase">
              {code}
            </DavidStyledText>
          </PaintBrushStroke>
          <DavidStyledText className="text-[16px]">
            {t('whileSuppliesLastOnePerAdultTicket', 'while supplies last, 1 per adult ticket')}
          </DavidStyledText>

          <SpecificTimesToolTip
            textColor={'black'}
            buttonColor={'core-gray-800'}
            buttonClassName="flex items-center focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent whitespace-nowrap mt-2"
            buttonStyle={{ padding: 0 }}
            copy={[t('validForShowtimesFromDec18ToDec21', 'Valid for Showtimes from 12/18 to 12/21')]}
          >
            <DavidStyledText className="mt-[1px] text-[16px]" color="core-gray-800">
              {t('onSelectShowtimes', 'On Select Showtimes')}
            </DavidStyledText>
          </SpecificTimesToolTip>
        </div>

        <div className="mt-6 flex flex-col items-center gap-3 text-center">
          <Button
            onClick={handleCopyCode}
            className="flex w-fit items-center gap-1"
            style={{ backgroundColor: accentColor2 }}
            variant="black"
          >
            {copiedCode ? <CheckIcon size={24} color="white" /> : <CopyIcon size={24} color="white" />}
            {copiedCode ? t('copiedCode', 'Copied Code') : t('copyCode', 'Copy Code')}
          </Button>
          <CaptionSM italic color="black">
            {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
          </CaptionSM>
        </div>
      </div>
    </Modal>
  )
}

export default DavidKidsGoFreeModal
